import React, { useState, useRef, useEffect } from "react";
import './HowToContainer.css';
import { FaFileAlt, FaUserCheck, FaCog } from 'react-icons/fa';

const HowToContainer = () => {
  const [hovered, setHovered] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const galleryRef = useRef(null);

  let galleryClass = '';
  if (hovered === 'second') galleryClass = ' howto-gallery-hovered-second';
  if (hovered === 'third') galleryClass = ' howto-gallery-hovered-third';
  if (hovered === 'featured') galleryClass = ' howto-gallery-hovered-featured';

  // Simplified handlers - let browser handle natural scrolling
  const handleTouchStart = () => {
    if (window.innerWidth > 768) return;
    setIsDragging(true);
  };

  const handleTouchEnd = () => {
    if (window.innerWidth > 768) return;
    setIsDragging(false);
  };

  const handleMouseDown = () => {
    if (window.innerWidth > 768) return;
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    if (window.innerWidth > 768) return;
    setIsDragging(false);
  };

  // Handle window resize to reset mobile behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setCurrentSlide(0);
        setIsDragging(false);
      }
    };

    const handleScroll = () => {
      if (window.innerWidth <= 768 && galleryRef.current) {
        clearTimeout(window.scrollTimeout);
        window.scrollTimeout = setTimeout(() => {
          if (galleryRef.current) {
            const scrollLeft = galleryRef.current.scrollLeft;
            const itemWidth = 280; // Fixed width for tablets
            const currentIndex = Math.round(scrollLeft / itemWidth);
            const maxIndex = galleryRef.current.children.length - 1;
            setCurrentSlide(Math.max(0, Math.min(maxIndex, currentIndex)));
          }
        }, 50);
      }
    };

    window.addEventListener('resize', handleResize);
    const currentGalleryRef = galleryRef.current;
    if (currentGalleryRef) {
      currentGalleryRef.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (currentGalleryRef) {
        currentGalleryRef.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <div className="howto-works-section" id="how-it-works">
      <div className="howto-container">
        <h2>Your 3-Step Path <br className="mobile-break"/>to Getting Hired</h2>

        <p className="howto-subtitle">Join engineers who've landed roles 3x faster <br className="mobile-break"/>using our proven system.</p>

        <div 
          className={`howto-image-gallery${galleryClass}`}
          ref={galleryRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={window.innerWidth <= 768 ? { 
            cursor: isDragging ? 'grabbing' : 'grab'
          } : {}}
        > 
          <div
            className={`howto-gallery-item howto-featured${hovered ? ' howto-gallery-shrink' : ''}`}
            onMouseEnter={() => setHovered('featured')}
            onMouseLeave={() => setHovered(null)}
            style={hovered === 'featured' ? { zIndex: 2 } : {}}
          >
            <div className="howto-gallery-image-wrapper">
              <img src="/Step1.png" alt="Resume tailoring process" />
            </div>
              <div className="howto-gallery-content">
                <div className="howto-content-header">
                  <FaFileAlt className="howto-icon" />
                  <h4 className="howto-feature-heading">Get Past ATS in 60 Seconds</h4>
                </div>
                <p>Get your mechanical, aerospace, or civil engineering resume perfectly customized for any job posting with AI-powered optimization and ATS compatibility.</p>
              </div>
          </div>

          <div
            className={`howto-gallery-item${hovered === 'second' ? ' howto-gallery-expanded' : hovered ? ' howto-gallery-shrink' : ''}`}
            onMouseEnter={() => setHovered('second')}
            onMouseLeave={() => setHovered(null)}
            style={hovered === 'second' ? { zIndex: 2 } : {}}
          >
            <div className="howto-gallery-image-wrapper">
              <img src="/Step2.png" alt="Interview practice interface" />
            </div>
              <div className="howto-gallery-content">
                <div className="howto-content-header">
                  <FaUserCheck className="howto-icon" />
                  <h4 className="howto-feature-heading">Ace Behavioral Interviews Confidently</h4>
                </div>
                <p>Master behavioral interviews with engineering-focused questions about design thinking, problem-solving, and project management. Get real-time feedback on your responses.</p>
              </div>
          </div>

          <div
            className={`howto-gallery-item${hovered === 'third' ? ' howto-gallery-expanded' : hovered ? ' howto-gallery-shrink' : ''}`}
            onMouseEnter={() => setHovered('third')}
            onMouseLeave={() => setHovered(null)}
            style={hovered === 'third' ? { zIndex: 2 } : {}}
          >
            <div className="howto-gallery-image-wrapper">
              <img src="/Step3.png" alt="Performance analytics" />
            </div>
              <div className="howto-gallery-content">
                <div className="howto-content-header">
                  <FaCog className="howto-icon howto-icon-large" />
                  <h4 className="howto-feature-heading">Master Technical Challenges with AI Feedback</h4>
                </div>
                <p>Master design challenges, system architecture, and technical scenarios with visual problem-solving. Practice voice responses and get instant AI feedback tailored for engineers.</p>
              </div>
          </div>
        </div>

        {/* Mobile slide indicators */}
        {window.innerWidth <= 768 && (
          <div className="howto-mobile-indicators">
            {[0, 1, 2].map((index) => (
              <button
                key={index}
                className={`howto-indicator ${currentSlide === index ? 'active' : ''}`}
                onClick={() => {
                  setCurrentSlide(index);
                  if (galleryRef.current) {
                    const itemWidth = window.innerWidth <= 480 ? 240 : 280;
                    galleryRef.current.scrollTo({
                      left: index * itemWidth,
                      behavior: 'smooth'
                    });
                  }
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HowToContainer;